import type { StructuredFilter } from "@media-viewer/shared";
import { filterToChips, type Chip } from "../search/filter-chip-model";

interface FilterChipsProps {
  filter: StructuredFilter | null;
  setFilter: (f: StructuredFilter | null) => void;
  pinnedKind?: "photo" | "video" | null;
}

const TONE_CLASS: Record<Chip["tone"], string> = {
  neutral: "bg-zinc-800 text-zinc-200 border-zinc-700",
  include: "bg-emerald-900/40 text-emerald-200 border-emerald-700",
  exclude: "bg-rose-900/40 text-rose-200 border-rose-700",
  exif: "bg-sky-900/40 text-sky-200 border-sky-700",
  favorite: "bg-amber-900/40 text-amber-200 border-amber-700",
  warning: "bg-yellow-900/40 text-yellow-200 border-yellow-700",
};

export function FilterChips({ filter, setFilter, pinnedKind }: FilterChipsProps) {
  if (!filter) return null;
  const chips = filterToChips(filter, pinnedKind);
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 px-4 pt-2 text-xs">
      {chips.map((chip) => (
        <span
          key={chip.id}
          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 ${TONE_CLASS[chip.tone]}`}
        >
          {chip.label}
          {chip.tone !== "warning" && (
            <button
              type="button"
              onClick={() => {
                const next = chip.remove(filter);
                // If the filter is now meaningfully empty, clear it entirely.
                const remaining = filterToChips(next, pinnedKind).filter((c) => c.tone !== "warning");
                setFilter(remaining.length > 0 ? next : null);
              }}
              className="text-zinc-400 hover:text-zinc-100"
              aria-label={`remove ${chip.label}`}
            >
              ×
            </button>
          )}
        </span>
      ))}
      <button
        type="button"
        onClick={() => setFilter(null)}
        className="ml-1 rounded bg-zinc-800 px-2 py-0.5 text-zinc-400 hover:text-zinc-100"
      >
        Clear all
      </button>
    </div>
  );
}
